import { restartWorkflow, revertTakeover } from '@luzula/luzula-lib';

/** Ziel-Projekt einer Übernahme. */
export interface TakeoverTarget {
	id: string;
	name: string;
}

/** Ziel-Projekt der Rücknahme (null → kein Projekt mehr → Bestand). */
export interface RevertRestoreTarget {
	id: string;
	name?: string;
}

/**
 * Einfachmodell-Übernahme: hängt einen Altpunkt (Bestand `null` oder
 * `abgeschlossen`) ins Ziel-Projekt **um** statt ihn zusätzlich zuzuordnen.
 *
 * - Workflow-Reset ins Ziel-Projekt (`restartWorkflow`: Status → `anstehend`,
 *   d.h. in den Pool ohne Inhaber — ein Bearbeiter „greift" ihn danach;
 *   `workflow.projectId` = Ziel, Reset-Marker mit `fromProjectId`).
 * - `header.projects = [Ziel]` (genau ein Projekt — der Punkt verlässt alle
 *   anderen).
 * - Der Entzug aus dem alten Projekt wird namentlich im Reset-Marker
 *   (`fromProjectName`) festgehalten (restartWorkflow speichert nur die ID).
 *
 * Mutiert `point`. Liefert die IDs der Projekte, die der Punkt **verlässt**
 * (Mitgliedschaft + bisheriges Workflow-Projekt, ohne das Ziel) — der Aufrufer
 * zieht deren abgeleiteten Projekt-Status nach.
 */
export function applyTakeoverMove(
	point: any,
	target: TakeoverTarget,
	user: string,
	userId: string
): string[] {
	if (!point.header) point.header = {};
	const oldMemberships: { id: string; name: string }[] =
		(point.header.projects ?? []).filter((p: any) => p?.id !== target.id);
	const oldWorkflowProjectId: string | undefined = point.workflow?.projectId;
	// Name des Quell-Projekts: bevorzugt das Projekt, dem der Workflow gehört;
	// sonst die erste Fremd-Mitgliedschaft.
	const fromName =
		oldMemberships.find(p => p.id === oldWorkflowProjectId)?.name
		?? oldMemberships[0]?.name;

	restartWorkflow(point, {
		toProjectId: target.id,
		toProjectName: target.name,
		// Verlassenes Projekt: setzt sowohl den Reset-Marker-Namen als auch den
		// closedProjects-Historieneintrag (Lebenszyklus 2026-05-27).
		fromProjectName: fromName,
		user,
		userId
	});
	point.header.projects = [{ id: target.id, name: target.name }];

	const leaving = new Set<string>();
	for (const p of oldMemberships) leaving.add(p.id);
	if (oldWorkflowProjectId && oldWorkflowProjectId !== target.id) leaving.add(oldWorkflowProjectId);
	return [...leaving];
}

/**
 * Einfachmodell-Rücknahme der Übernahme (Umkehrung von `applyTakeoverMove`,
 * User-Wunsch 2026-06-19): setzt einen per Übernahme `anstehend` gewordenen
 * Punkt zurück auf `abgeschlossen` und hängt ihn wieder ins vorige Projekt
 * (`restoreTo`) um.
 *
 * - Workflow-Rücknahme (`revertTakeover`: Status → `abgeschlossen`,
 *   `projectId` = voriges Projekt; History bekommt `anstehend → abgeschlossen`).
 * - `header.projects = [voriges Projekt]`.
 * - Gibt es das Projekt nicht mehr (`restoreTo === null`), wird der Punkt
 *   projektlos → Bestand: `header.projects = []`, kein `workflow.projectId`.
 *
 * Mutiert `point`. Liefert die ID des Projekts, das der Punkt **verlässt** (das
 * Projekt, in dem er `anstehend` lag) — der Aufrufer zieht dessen abgeleiteten
 * Status nach (und den des Ziel-Projekts separat, analog `freigeben`).
 */
export function applyTakeoverRevert(
	point: any,
	restoreTo: RevertRestoreTarget | null,
	user: string,
	userId: string
): string[] {
	if (!point.header) point.header = {};
	const leftProjectId: string | undefined = point.workflow?.projectId;
	revertTakeover(point, {
		restoreProjectId: restoreTo?.id,
		restoreProjectName: restoreTo?.name,
		user,
		userId
	});
	point.header.projects = restoreTo ? [{ id: restoreTo.id, name: restoreTo.name }] : [];

	const leaving: string[] = [];
	if (leftProjectId && leftProjectId !== restoreTo?.id) leaving.push(leftProjectId);
	return leaving;
}
